import React, { createContext, useContext, useState, useCallback } from 'react';
import pushResendCallbacks, { PushResendCallbacksResponse } from '../services/pushResendCallbacks';

interface IPushResendCallbacksContext {
  cnjIds: string[];
  setCnjIds: (data: string[]) => void;
  results: PushResendCallbacksResponse | null;
  setResults: (data: PushResendCallbacksResponse | null) => void;
  resendCallbacks: (cnjIds: string[]) => Promise<PushResendCallbacksResponse>;
  loading: boolean;
  error: string | null;
}

export const PushResendCallbacksContext = createContext<IPushResendCallbacksContext>({
  cnjIds: [],
  setCnjIds: () => {},
  results: null,
  setResults: () => {},
  resendCallbacks: async () => ({ success: false, data: [] }),
  loading: false,
  error: null,
});

const PushResendCallbacksProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cnjIds, setCnjIdsState] = useState<string[]>([]);
  const [results, setResults] = useState<PushResendCallbacksResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Wrapper para setCnjIds que limpa os resultados anteriores quando novos IDs são definidos
  const setCnjIds = useCallback((ids: string[]) => {
    // Limpa resultados anteriores quando novos IDs são definidos
    setResults(null);
    setError(null);
    setCnjIdsState(ids);
  }, []);

  const resendCallbacks = useCallback(async (ids: string[]): Promise<PushResendCallbacksResponse> => {
    try {
      setLoading(true);
      setError(null);
      const response = await pushResendCallbacks.resendCallbacks({ cnjIds: ids });
      setResults(response);
      return response;
    } catch (err: any) {
      console.error('Erro ao reenviar callbacks Push:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Erro ao reenviar callbacks Push';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <PushResendCallbacksContext.Provider
      value={{
        cnjIds,
        setCnjIds,
        results,
        setResults,
        resendCallbacks,
        loading,
        error,
      }}
    >
      {children}
    </PushResendCallbacksContext.Provider>
  );
};

const usePushResendCallbacks = (): IPushResendCallbacksContext => {
  const context = useContext(PushResendCallbacksContext);
  if (!context) {
    throw new Error('usePushResendCallbacks must be used within a PushResendCallbacksProvider');
  }
  return context;
};

export { PushResendCallbacksProvider, usePushResendCallbacks };

